var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _promise, _resolve, _throttledCallback, _endCallback, _onScrollInit, _onScrollEnd, _userEvent, _ignore, _isScrolling, _Scroller_instances, scroll_fn, edge_get, setup_fn, _reset, _handleScroll, _handleScrollEnd, _rafId, _handleScroll2, _update, _resizeObserver;
import { debounce, throttle, prefersReducedMotion } from "@theme/utilities";
const SCROLL_END_TIMEOUT = 50;
class Scroller {
  /**
   * Creates a Scroller instance.
   *
   * @param {HTMLElement} element - The element to apply scrolling to.
   * @param {Object} options - The options for the scroller.
   * @param {() => void} options.onScroll - Function to call while scrolling and after scrolling ends.
   * @param {() => void} [options.onScrollStart] - Function to call when scrolling starts.
   * @param {() => void} [options.onScrollEnd] - Function to call after scrolling ends.
   */
  constructor(element, options) {
    __privateAdd(this, _Scroller_instances);
    /**
     * The element to apply scrolling to.
     * @type {HTMLElement}
     */
    __publicField(this, "element");
    /**
     * Promise that resolves when scrolling ends.
     * @type {Promise<void>|undefined}
     */
    __privateAdd(this, _promise);
    /**
     * Function to resolve the scroll end promise.
     * @type {Function|undefined}
     */
    __privateAdd(this, _resolve);
    /**
     * Callback function to call while user is scrolling, throttled every 50ms.
     * @type {() => void}
     */
    __privateAdd(this, _throttledCallback);
    /**
     * Callback function invoked after user scroll ends.
     * @type {() => void}
     */
    __privateAdd(this, _endCallback);
    /**
     * Callback function invoked when scrolling starts, regardless of whether it was triggered by a user event.
     * @type {(() => void) | undefined}
     */
    __privateAdd(this, _onScrollInit);
    /**
     * Callback function invoked when scrolling ends, regardless of whether it was triggered by a user event.
     * @type {(() => void) | undefined}
     */
    __privateAdd(this, _onScrollEnd);
    /**
     * Whether the scroll was triggered by a user event.
     * @type {boolean}
     */
    __privateAdd(this, _userEvent, true);
    /**
     * Whether the next scroll event should be ignored.
     * @type {boolean}
     */
    __privateAdd(this, _ignore, false);
    /**
     * Whether the element is currently scrolling.
     * @type {boolean}
     */
    __privateAdd(this, _isScrolling, false);
    __privateAdd(this, _reset, () => {
      var _a;
      __privateGet(this, _handleScrollEnd).cancel();
      (_a = __privateGet(this, _resolve)) == null ? void 0 : _a.call(this);
      __privateSet(this, _promise, void 0);
      __privateSet(this, _resolve, void 0);
      __privateSet(this, _userEvent, true);
      __privateSet(this, _ignore, false);
    });
    /**
     * Event handler for the 'scroll' event.
     */
    __privateAdd(this, _handleScroll, () => {
      var _a;
      if (!__privateGet(this, _isScrolling)) {
        (_a = __privateGet(this, _onScrollInit)) == null ? void 0 : _a.call(this);
        __privateSet(this, _isScrolling, true);
      }
      if (__privateGet(this, _ignore)) {
        __privateGet(this, _reset).call(this);
        __privateGet(this, _handleScrollEnd).call(this, false);
        return;
      }
      const userEvent = __privateGet(this, _userEvent);
      __privateMethod(this, _Scroller_instances, setup_fn).call(this);
      if (userEvent) __privateGet(this, _throttledCallback).call(this);
      __privateGet(this, _handleScrollEnd).call(this, userEvent);
    });
    /**
     * Handler called when scrolling has ended.
     */
    __privateAdd(this, _handleScrollEnd, debounce(
      /**
       * @param {boolean} userEvent
       */
      (userEvent) => {
        var _a, _b;
        (_a = __privateGet(this, _resolve)) == null ? void 0 : _a.call(this);
        if (userEvent) __privateGet(this, _endCallback).call(this);
        __privateGet(this, _reset).call(this);
        if (__privateGet(this, _isScrolling)) {
          (_b = __privateGet(this, _onScrollEnd)) == null ? void 0 : _b.call(this);
          __privateSet(this, _isScrolling, false);
        }
      },
      SCROLL_END_TIMEOUT
    ));
    __privateSet(this, _throttledCallback, throttle(options.onScroll, SCROLL_END_TIMEOUT));
    __privateSet(this, _endCallback, options.onScroll);
    __privateSet(this, _onScrollInit, options.onScrollStart);
    __privateSet(this, _onScrollEnd, options.onScrollEnd);
    this.element = element;
    this.element.addEventListener("scroll", __privateGet(this, _handleScroll));
  }
  /**
   * Scrolls to a specific position or element.
   * @param {number | HTMLElement} input - The position in pixels or an element to scroll to.
   * @param {Object} [options] - Options for the scroll.
   * @param {boolean} [options.instant] - Whether to scroll instantly.
   */
  async to(input, options) {
    let value;
    if (input instanceof HTMLElement) {
      const paddingStart = calculatePaddingStart(this.element, this.axis);
      value = input[`offset${__privateGet(this, _Scroller_instances, edge_get)}`] - paddingStart;
    } else {
      value = input;
    }
    const currentPosition = this.element[`scroll${__privateGet(this, _Scroller_instances, edge_get)}`];
    const willChange = currentPosition !== value;
    if (willChange) {
      __privateMethod(this, _Scroller_instances, scroll_fn).call(this, { ...options, method: "scrollTo", value });
    } else if (__privateGet(this, _isScrolling)) {
      __privateGet(this, _handleScrollEnd).call(this, false);
    }
  }
  /**
   * Scrolls by a certain number of pixels.
   * @param {number} value - The number of pixels to scroll by.
   * @param {Object} [options] - Options for the scroll.
   * @param {boolean} [options.instant] - Whether to scroll instantly.
   */
  by(value, options) {
    __privateMethod(this, _Scroller_instances, scroll_fn).call(this, { ...options, method: "scrollBy", value });
  }
  /**
   * Gets the scrolling axis ('x' or 'y') based on the element's dimensions.
   * @type {'x' | 'y'}
   * @readonly
   */
  get axis() {
    return getScrollAxis(this.element);
  }
  /**
   * Promise that resolves when scrolling ends.
   * @type {Promise<void>}
   * @readonly
   */
  get finished() {
    return __privateGet(this, _promise) ?? Promise.resolve();
  }
  /**
   * Sets the scroll snap behavior of the element.
   * @param {boolean} value - Whether to enable scroll snap.
   */
  set snap(value) {
    __privateSet(this, _ignore, true);
    this.element.style.setProperty("scroll-snap-type", value ? `${this.axis} mandatory` : "none");
  }
  /**
   * Destroys the Scroller instance.
   */
  destroy() {
    this.element.removeEventListener("scroll", __privateGet(this, _handleScroll));
  }
}
_promise = new WeakMap();
_resolve = new WeakMap();
_throttledCallback = new WeakMap();
_endCallback = new WeakMap();
_onScrollInit = new WeakMap();
_onScrollEnd = new WeakMap();
_userEvent = new WeakMap();
_ignore = new WeakMap();
_isScrolling = new WeakMap();
_Scroller_instances = new WeakSet();
/**
 * Scrolls the element.
 * @param {Object} options - The options for the scroll.
 * @param {'scrollTo' | 'scrollBy'} options.method - The method to use to scroll.
 * @param {number} options.value - The value to scroll to.
 * @param {boolean} [options.instant] - Whether to scroll instantly.
 */
scroll_fn = function(options) {
  const { method, value, instant = prefersReducedMotion() } = options;
  __privateGet(this, _reset).call(this);
  __privateSet(this, _ignore, instant);
  __privateSet(this, _userEvent, false);
  const currentPosition = this.element[`scroll${__privateGet(this, _Scroller_instances, edge_get)}`];
  const targetPosition = method === "scrollBy" ? currentPosition + value : value;
  const scrollDistance = Math.abs(targetPosition - currentPosition);
  if (scrollDistance < 1) {
    return Promise.resolve();
  }
  if (!instant) __privateMethod(this, _Scroller_instances, setup_fn).call(this);
  this.element[method]({
    [__privateGet(this, _Scroller_instances, edge_get).toLowerCase()]: value,
    behavior: instant ? "instant" : "smooth"
  });
};
edge_get = function() {
  return this.axis === "x" ? "Left" : "Top";
};
/**
 * Sets up the scroll end promise if not already set.
 */
setup_fn = function() {
  if (__privateGet(this, _promise)) {
    return;
  }
  __privateSet(this, _promise, new Promise((resolve) => __privateSet(this, _resolve, resolve)));
};
_reset = new WeakMap();
_handleScroll = new WeakMap();
_handleScrollEnd = new WeakMap();
function getScrollAxis(el) {
  if (el.scrollHeight > el.clientHeight && el.scrollWidth === el.clientWidth) {
    return "y";
  }
  if (el.scrollWidth > el.clientWidth && el.scrollHeight === el.clientHeight) {
    return "x";
  }
  return el.scrollWidth > el.scrollHeight ? "x" : "y";
}
function calculatePaddingStart(element, axis) {
  const computedStyle = getComputedStyle(element);
  const value = axis === "x" ? computedStyle.paddingInlineStart : computedStyle.paddingBlockStart;
  return parseFloat(value);
}
function scrollIntoView(element, { ancestor, behavior = "smooth", block = "start", inline = "start" } = {}) {
  if (!ancestor) {
    return element.scrollIntoView({ behavior, block, inline });
  }
  const elemRect = element.getBoundingClientRect();
  const ancestorRect = ancestor.getBoundingClientRect();
  const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
    switch (alignment) {
      case "start":
        return currentScroll + elemStart - ancestorStart;
      case "center":
        return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
      case "end":
        return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
      default:
        return currentScroll;
    }
  };
  const scrollTop = ancestor.scrollHeight > ancestor.clientHeight ? calculateScrollOffset(
    block,
    ancestorRect.top,
    ancestor.clientHeight,
    elemRect.top,
    elemRect.height,
    ancestor.scrollTop
  ) : ancestor.scrollTop;
  const scrollLeft = ancestor.scrollWidth > ancestor.clientWidth ? calculateScrollOffset(
    inline,
    ancestorRect.left,
    ancestor.clientWidth,
    elemRect.left,
    elemRect.width,
    ancestor.scrollLeft
  ) : ancestor.scrollLeft;
  ancestor.scrollTo({ top: scrollTop, left: scrollLeft, behavior });
}
class ScrollHint extends HTMLElement {
  constructor() {
    super(...arguments);
    /** @type {number | null} */
    __privateAdd(this, _rafId, null);
    __privateAdd(this, _handleScroll2, () => {
      if (__privateGet(this, _rafId) !== null) return;
      __privateSet(this, _rafId, requestAnimationFrame(() => {
        __privateSet(this, _rafId, null);
        __privateGet(this, _update).call(this);
      }));
    });
    __privateAdd(this, _update, () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = this;
      const scrollDirection = scrollWidth > clientWidth ? "horizontal" : "vertical";
      const scrollPercentage = scrollDirection === "vertical" ? scrollTop / (scrollHeight - clientHeight) : scrollLeft / (scrollWidth - clientWidth);
      this.style.maskImage = Number.isNaN(scrollPercentage) ? "" : `linear-gradient(
        to ${scrollDirection === "vertical" ? "bottom" : "right"},
        transparent ${scrollPercentage > 0 ? 1 : 0}%,
        black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
        black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
        transparent 100%
      )`;
    });
    __privateAdd(this, _resizeObserver, new ResizeObserver(__privateGet(this, _update)));
  }
  connectedCallback() {
    this.addEventListener("scroll", __privateGet(this, _handleScroll2));
    __privateGet(this, _resizeObserver).observe(this);
  }
  disconnectedCallback() {
    this.removeEventListener("scroll", __privateGet(this, _handleScroll2));
    __privateGet(this, _resizeObserver).disconnect();
    if (__privateGet(this, _rafId) !== null) {
      cancelAnimationFrame(__privateGet(this, _rafId));
      __privateSet(this, _rafId, null);
    }
  }
}
_rafId = new WeakMap();
_handleScroll2 = new WeakMap();
_update = new WeakMap();
_resizeObserver = new WeakMap();
if (!customElements.get("scroll-hint")) {
  customElements.define("scroll-hint", ScrollHint);
}
export {
  Scroller,
  scrollIntoView
};
